/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { CleanIconButton, LoadingSpinner, Tooltip } from '@neo4j-ndl/react';
import { XMarkIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';

import { type CommonProps } from '../../_common/types';

type FileTagProps = {
  /** The icon that represents the file type */
  icon: React.ReactNode;
  /** The name of the file */
  fileName: string;
  /** The type of the file, e.g. 'pdf', 'docx', 'txt', etc. */
  fileType: string;
  /** Callback function triggered either when the file tag is clicked, or when the remove button is clicked (if isRemovable is true) */
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /** Whether the file tag is removable */
  isRemovable?: boolean;
  /** Whether the file tag is loading */
  isLoading?: boolean;
};

/**
 * The component is used to display a file in a chat, or in a prompt.
 * It can be removable and it can be loading.
 *
 * @alpha - Changes to this component may be breaking.
 */
const FileTag = ({
  onClick,
  isRemovable = false,
  fileName,
  fileType,
  icon,
  className,
  style,
  htmlAttributes,
  ref,
  isLoading = false,
  ...restProps
}: CommonProps<React.ElementType, FileTagProps>) => {
  const Component = isRemovable !== true ? 'button' : 'div';
  const classes = classNames('ndl-ai-file-tag', className);

  const file = `${fileName}.${fileType}`;

  return (
    <Tooltip
      type="simple"
      placement="bottom"
      hoverDelay={{ close: 0, open: 500 }}
    >
      <Tooltip.Trigger hasButtonWrapper>
        <Component
          ref={ref}
          className={classes}
          style={style}
          {...(isRemovable ? {} : { onClick })}
          // what is read by screen readers here?
          {...restProps}
          {...htmlAttributes}
        >
          <div className="ndl-ai-file-tag-leading">
            {isLoading ? <LoadingSpinner size="medium" /> : icon}
          </div>
          <div className="ndl-ai-file-tag-content">
            <span className="ndl-ai-file-tag-content-title">{fileName}</span>
            <span className="ndl-ai-file-tag-content-type">
              {fileType?.toUpperCase()}
            </span>
          </div>
          {isRemovable && (
            <CleanIconButton
              onClick={onClick}
              description={null}
              htmlAttributes={{
                'aria-label': `Remove ${file}`,
              }}
              size="small"
              className="ndl-ai-file-tag-remove"
            >
              <XMarkIconOutline />
            </CleanIconButton>
          )}
        </Component>
      </Tooltip.Trigger>
      <Tooltip.Content>{file}</Tooltip.Content>
    </Tooltip>
  );
};

export { FileTag };
